import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Paper,
} from '@mui/material';
import {
  Business,
  Analytics,
  Cloud,
  Timeline,
  CheckCircle,
  TrendingUp,
  Security,
  // Integration,
  IntegrationInstructions,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import coreServiceOfferingsData from '../data/services/coreServiceOfferingsData.json';
import whyChooseOurServicesData from '../data/services/whyChooseOurServicesData.json';
import serviceProcessData from '../data/services/serviceProcessData.json';

const Services = () => {

  const coreServiceOfferingsIconMap = {
    business: <Business sx={{ fontSize: 50 }} />,
    analytics: <Analytics sx={{ fontSize: 50 }} />,
    cloud: <Cloud sx={{ fontSize: 50 }} />,
    timeline: <Timeline sx={{ fontSize: 50 }} />,
  };

  const whyChooseOurServicesDataIconMap = {
    checkCircle: <CheckCircle sx={{ fontSize: 40 }} />,
    trendingUp: <TrendingUp sx={{ fontSize: 40 }} />,
    security: <Security sx={{ fontSize: 40 }} />,
    integrationInstructions: <IntegrationInstructions sx={{ fontSize: 40 }} />,
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', md: '3rem' },
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
              }}
            >
              Comprehensive IT Talent Solutions for Your Business Needs
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Main Services */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Core Service Offerings
        </Typography>
        <Grid container spacing={4}>
          {coreServiceOfferingsData.map((service, index) => (
            <Grid item xs={12} lg={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: service.color,
                          width: 70,
                          height: 70,
                          mr: 2,
                        }}
                      >
                        {/* {service.icon} */}
                        {coreServiceOfferingsIconMap[service.icon]}
                      </Avatar>
                      <Typography variant="h4" component="h3" fontWeight="bold">
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                      {service.description}
                    </Typography>

                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Key Modules & Technologies:
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {service.modules.map((module, moduleIndex) => (
                        <Chip
                          key={moduleIndex}
                          label={module}
                          size="small"
                          sx={{ m: 0.5, bgcolor: 'grey.100' }}
                        />
                      ))}
                    </Box>

                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Key Benefits:
                    </Typography>
                    <List dense>
                      {service.benefits.map((benefit, benefitIndex) => (
                        <ListItem key={benefitIndex} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={benefit} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      sx={{ bgcolor: service.color }}
                      fullWidth
                    >
                      Learn More
                    </Button>
                  </CardActions>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Service Features */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold' }}
          >
            Why Choose Our Services?
          </Typography>
          <Grid container spacing={4}>
            {whyChooseOurServicesData.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex' }}>
                <motion.div
                  style={{ width: '100%', display: 'flex', flex: 1 }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 70,
                        height: 70,
                        mb: 2,
                      }}
                    >
                      {whyChooseOurServicesDataIconMap[feature.icon]}
                    </Avatar>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="bold"
                      align="center"
                      sx={{ minHeight: 48 }} // Optional: forces title height
                    >
                      {feature.title}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>

            ))}
          </Grid>
        </Container>
      </Box>

      {/* Service Process */}
      {/* <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
                color: 'text.primary',
                fontWeight: 'bold',
                mb: 6,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  backgroundColor: 'primary.main',
                  borderRadius: '2px',
                },
              }}
        >
          Our Service Process
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              step: '01',
              title: 'Requirement Analysis',
              description: 'Understanding your specific needs and project requirements',
            },
            {
              step: '02',
              title: 'Talent Matching',
              description: 'Identifying and screening the best-fit consultants for your project',
            },
            {
              step: '03',
              title: 'Quick Deployment',
              description: 'Rapid onboarding and deployment of selected resources',
            },
            {
              step: '04',
              title: 'Ongoing Support',
              description: 'Continuous monitoring and support throughout the engagement',
            },
          ].map((process, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {process.step}
                  </Avatar>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    {process.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {process.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container> */}

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Our Service Process
        </Typography>

        <Grid container spacing={4}>
          {serviceProcessData.map((process, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {process.step}
                  </Avatar>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    {process.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {process.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 300 }}>
            Let us help you find the right talent for your next project
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              Contact Us Today
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              View Case Studies
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Services;